<!--
Fork addition: right-hand inspector. Clicking a factory on the board
selects it (see useInspectorSelection); this panel shows its details and
the controls Philip asked for in one place — recipe picker, count,
rotate/flip, objective, io rates, delete. With nothing selected it shows
a hint plus basic blueprint counts.
-->
<script setup lang="ts">
import {computed, reactive, ref, unref} from 'vue';
import {GameRecipeIOFlags} from '#types/constants';
import type {GameItem, GameRecipe, GameRecipeIO} from '#types/game-data';
import {injectBlueprintModel, type BlueprintItemModel, type RecipeIOModel} from '@/scripts/model/store';
import {injectGameData} from '@/scripts/data';
import {injectSettings} from '@/scripts/settings';
import {Objective, type FlowOrientation, type ObjectiveType} from '@/scripts/types';
import {formatIo, formatNumber} from '@/scripts/format';
import {Point, Rect} from '@/scripts/geometry';
import {snapPointToGrid} from '@/scripts/grid';
import {COI_ITEM_CATEGORY} from '@/scripts/coi-categories';
import {transportSuggestion} from '@/scripts/transport';
import {itemFlowStatus} from '@/scripts/flow-status';
import {useInspectorSelection, useLastPlacedFactory} from '@/composables';
import {getScrollBox, screenToClient, useSharedBlueprintSurface} from '@/composables/drag-helpers';
import {
    mdiArrowDown,
    mdiArrowLeft,
    mdiArrowRight,
    mdiArrowUp,
    mdiBullseye,
    mdiBullseyeArrow,
    mdiCheck,
    mdiCircleOutline,
    mdiDelete,
    mdiLinkOff,
    mdiMinusThick,
    mdiPlus,
    mdiPlusThick,
    mdiPriorityLow,
} from '@mdi/js';

const blueprintModel = injectBlueprintModel();
const gameData = injectGameData();
const settings = injectSettings();
const {selectedItemKey, previewItemName, selectItem, clearSelection, setPreview} = useInspectorSelection();
const {setLastPlaced} = useLastPlacedFactory();
const {surfaceElem, originElem, updateSurface, boundingRect} = useSharedBlueprintSurface();

const selectedItem = computed<BlueprintItemModel | undefined>(() => {
    //depend on the generation number so deletion/loading refreshes us
    void blueprintModel.itemsGenerationNumber;
    const key = unref(selectedItemKey);
    return key ? blueprintModel.itemByKey(key) : undefined;
});

const itemCount = computed(() => {
    void blueprintModel.itemsGenerationNumber;
    return [...blueprintModel.items].length;
});
const linkCount = computed(() => {
    void blueprintModel.summaryGenerationNumber;
    return [...blueprintModel.links].length;
});

const allRecipes = computed<GameRecipe[]>(() => {
    const item = unref(selectedItem);
    return item ? [...item.recipes] : [];
});

//fork addition: recipe picker as a searchable lookahead — typing
//filters the factory's recipes by recipe name AND by any input/output
//product label, so a product name surfaces the recipes using it
interface RecipeOption {
    title: string;
    value: string;
    recipe: GameRecipe;
}
const recipeSearch = ref('');
//fork fix: the picker must never show the internal recipe key (the
//packaged data names recipes with short keys like "a4"). Human label:
//the recipe's longName when it has one, else a compact IO summary
//("Any Countable → Any Countable"), else the first output's label.
function recipeDisplayTitle(recipe: GameRecipe): string {
    if(recipe.longName)
        return recipe.longName;
    const ioLabels = (io: GameRecipeIO[]) => filterShownRecipeIo(io)
        .map((i) => i.product.label)
        .join(' + ');
    const inputs = ioLabels(recipe.input);
    const outputs = ioLabels(recipe.output);
    if(inputs || outputs)
        return `${inputs || '—'} → ${outputs || '—'}`;
    return recipe.name;
}
const recipeOptions = computed<RecipeOption[]>(() => unref(allRecipes).map((recipe) => ({
    title: recipeDisplayTitle(recipe),
    value: recipe.name,
    recipe,
})));
const filteredRecipeOptions = computed<RecipeOption[]>(() => {
    const query = unref(recipeSearch).trim().toLowerCase();
    const options = unref(recipeOptions);
    if(!query)
        return options;
    const ioMatches = (io: GameRecipeIO) => io.product.lowerLabel.indexOf(query) > -1;
    return options.filter(({title, recipe}) =>
        (title.toLowerCase().indexOf(query) > -1)
        || recipe.input.some(ioMatches)
        || recipe.output.some(ioMatches));
});
const selectedRecipeName = computed(() => unref(selectedItem)?.selectedRecipe?.name);
function onRecipePicked(name: string | null) {
    if(name)
        selectRecipe(name);
}

//fork addition: transport suggestion per io row (same logic as the
//link chips — see scripts/transport.ts)
function ioTransport(io: RecipeIOModel) {
    const product = io.name ? gameData.getGameItem(io.name) : undefined;
    return transportSuggestion(gameData, product, io.cpsSolvedTotal);
}

//fork addition: STARVED / BLOCKED diagnosis for the selected item
//(same helper as the card badge — see scripts/flow-status.ts). Only
//the flagged product's io row gains a warning line, spelling out the
//target rate against what the links actually carry.
const selectedFlowStatus = computed(() => {
    const item = unref(selectedItem);
    return item ? itemFlowStatus(item) : undefined;
});
function ioWarning(io: RecipeIOModel): {kind: 'starved' | 'blocked'; text: string} | undefined {
    const status = unref(selectedFlowStatus);
    if(!status || (status.io.key !== io.key))
        return undefined;
    if(status.kind === 'starved') {
        return {
            kind: 'starved',
            text: `needs ${formatIo(status.targetRate, io)} · linked supply ${formatIo(status.linkedRate, io)}`,
        };
    }
    return {
        kind: 'blocked',
        text: `makes ${formatIo(status.targetRate, io)} · taken ${formatIo(status.linkedRate, io)}`,
    };
}

function filterShownRecipeIo(io: GameRecipeIO[]) {
    return io.filter((i) => !(i.flags & GameRecipeIOFlags.HideInMenu));
}

function ioRate(io: RecipeIOModel) {
    const solved = formatIo(io.cpsSolvedTotal, io);
    const max = formatIo(io.cpsMaxTotal, io);
    //the max is noise when it equals the solved rate (the usual case) —
    //only show it when it actually differs
    if(solved && max && (max !== solved))
        return `${solved} / max ${max}`;
    return solved || max || '—';
}

const inputs = computed(() => unref(selectedItem)?.selectedRecipe?.visibleInput() || []);
const outputs = computed(() => unref(selectedItem)?.selectedRecipe?.visibleOutput() || []);

function changeCount(offset: number) {
    const item = unref(selectedItem);
    if(item)
        item.setCount(Math.max(item.count + offset, 0));
}
//fork change: orientation is a 4-way choice (LR/RL/TB/BT), replacing
//the old flip toggle — it drives which card edges the ports sit on
function setOrientation(orientation: FlowOrientation | null) {
    const item = unref(selectedItem);
    if(item && orientation)
        item.orientation = orientation;
}
function changeObjective(objective: ObjectiveType) {
    const item = unref(selectedItem);
    if(!item)
        return;
    item.setObjective(item.objective === objective ? undefined : objective);
}
function deleteItem() {
    const item = unref(selectedItem);
    if(!item)
        return;
    clearSelection();
    item.deleteThis();
}
function selectRecipe(name: string) {
    unref(selectedItem)?.selectRecipe(name);
}
function deleteAllLinks() {
    unref(selectedItem)?.deleteAllLinks();
}

//fork addition: upgrade / downgrade guidance — the immediate bigger
//and smaller brother within the factory's family. Families are
//derived from the game data, no hand-kept lists: group factories by
//label stem (trailing roman-numeral tier markers I/II/III/IV/V and
//parenthesised variant suffixes like "(large)" / "(gas)" stripped),
//order within a family by recipe tier, falling back to label order.
//Clicking a brother switches the inspector to its preview — guidance
//only, the board is never mutated from here.
function factoryStem(label: string): string {
    let stem = label;
    for(;;) {
        const next = stem
            .replace(/\s*\([^)]*\)\s*$/, '')
            .replace(/\s+(I|II|III|IV|V)$/, '');
        if(next === stem)
            return stem.trim();
        stem = next;
    }
}
interface FactoryNeighbours {
    upgrade?: GameItem;
    downgrade?: GameItem;
}
function factoryNeighbours(item: GameItem | undefined): FactoryNeighbours {
    if(!item)
        return {};
    const stem = factoryStem(item.label);
    const family = gameData.gameFactoriesArray
        .filter((factory) => factoryStem(factory.label) === stem)
        .sort((a, b) => ((a.recipe?.tier ?? 0) - (b.recipe?.tier ?? 0))
            || a.label.localeCompare(b.label));
    const index = family.findIndex((factory) => factory.name === item.name);
    if((index < 0) || (family.length < 2))
        return {};
    return {
        upgrade: family[index + 1],
        downgrade: family[index - 1],
    };
}
function gameItemOf(item: BlueprintItemModel | undefined): GameItem | undefined {
    return item?.name ? gameData.getGameItem(item.name) : undefined;
}
interface FamilyRow {
    kind: 'upgrade' | 'downgrade';
    factory: GameItem;
}
function familyRows(item: GameItem | undefined): FamilyRow[] {
    const {upgrade, downgrade} = factoryNeighbours(item);
    const rows: FamilyRow[] = [];
    if(downgrade)
        rows.push({kind: 'downgrade', factory: downgrade});
    if(upgrade)
        rows.push({kind: 'upgrade', factory: upgrade});
    return rows;
}
const selectedTier = computed(() => gameItemOf(unref(selectedItem))?.recipe?.tier);
const selectedFamilyRows = computed<FamilyRow[]>(() => familyRows(gameItemOf(unref(selectedItem))));

//fork addition: left-panel preview — a factory clicked in the toolbox
//(not yet on the board) is shown here with its static data, until a
//board item is selected or the factory is added
const previewItem = computed<GameItem | undefined>(() => {
    const name = unref(previewItemName);
    return name ? gameData.getGameItem(name) : undefined;
});
const previewCategory = computed(() => {
    const item = unref(previewItem);
    return item ? COI_ITEM_CATEGORY[item.name] : undefined;
});
const previewTier = computed(() => unref(previewItem)?.recipe?.tier);
const previewCost = computed(() => {
    const item = unref(previewItem);
    if(!item?.cost)
        return [];
    return item.cost.map((cost) => ({
        product: gameData.getGameItem(cost.name),
        count: cost.count,
    }));
});
const previewRecipes = computed<GameRecipe[]>(() => {
    const item = unref(previewItem);
    return item?.recipeDictionary?.recipes || [];
});
const previewFamilyRows = computed<FamilyRow[]>(() => familyRows(unref(previewItem)));

function addPreviewToBoard() {
    const gameItem = unref(previewItem);
    if(!gameItem)
        return;
    const item = reactive(blueprintModel.addItem(gameItem.name));
    //place it centred in the currently visible part of the board,
    //snapped to the grid (same placement path as a panel drop)
    let point = Point.assign({x: 100, y: 100});
    const {scrollboxElement} = getScrollBox(surfaceElem);
    if(scrollboxElement) {
        const visible = Rect.assign(scrollboxElement.getBoundingClientRect());
        const center = Rect.assign({
            x: visible.x + (visible.width / 2),
            y: visible.y + (visible.height / 2),
        });
        const client = screenToClient(originElem, center, settings.scale);
        //cards are 300px wide; offset so the card centre is the target
        point = Point.assign({x: client.x - 150, y: client.y - 75});
    }
    item.setRect(item.rect.assignPoint(snapPointToGrid(point)).limit(unref(boundingRect)));
    updateSurface(blueprintModel.items);
    setLastPlaced(gameItem.name);
    //switch the inspector to the placed item (also clears the preview)
    selectItem(item.key);
}
</script>

<template>
    <div
        class="inspector-panel"
        :class="{'inspector-active': !!selectedItem || !!previewItem}"
        @pointerdown.stop
        @click.stop
    >
        <!-- fork addition (Stage I): phone-only Back bar — the
             inspector is a full-screen step there, and this is the
             way back to the board (hidden above 900px) -->
        <div class="inspector-mobile-bar">
            <v-btn variant="text" size="small" :prepend-icon="mdiArrowLeft" @click="clearSelection()">
                Back
            </v-btn>
            <span class="text-subtitle-2 font-weight-bold text-truncate">
                {{ selectedItem?.label || previewItem?.label || 'Inspector' }}
            </span>
        </div>
        <template v-if="selectedItem">
            <div class="d-flex align-center ga-2 mb-1">
                <icon-component :image="selectedItem.image" :tooltip="selectedItem.label" />
                <div class="min-width-0">
                    <div class="text-subtitle-2 font-weight-bold text-truncate">
                        {{ selectedItem.label }}
                    </div>
                    <div class="text-caption text-medium-emphasis text-truncate">
                        {{ selectedItem.name }}
                        <template v-if="selectedTier !== undefined">
                            · Tier {{ selectedTier }}
                        </template>
                    </div>
                </div>
            </div>

            <div class="inspector-section">
                <div class="text-caption font-weight-bold text-medium-emphasis">
                    COUNT
                </div>
                <div class="d-flex align-center ga-2">
                    <v-btn size="small" variant="tonal" :icon="mdiMinusThick" @click="changeCount(-1)" />
                    <span class="text-body-2 font-weight-bold count-value">{{ selectedItem.count }}</span>
                    <v-btn size="small" variant="tonal" :icon="mdiPlusThick" @click="changeCount(1)" />
                    <span v-if="selectedItem.solvedCount !== undefined" class="text-caption text-medium-emphasis">
                        solved: {{ formatNumber(selectedItem.solvedCount) }}
                    </span>
                </div>
            </div>

            <div class="inspector-section">
                <div class="text-caption font-weight-bold text-medium-emphasis">
                    RECIPE
                </div>
                <v-autocomplete
                    :model-value="selectedRecipeName"
                    :items="filteredRecipeOptions"
                    :search="recipeSearch"
                    item-title="title"
                    item-value="value"
                    density="compact"
                    variant="outlined"
                    hide-details
                    no-filter
                    auto-select-first
                    placeholder="Search recipes or products…"
                    @update:search="recipeSearch = $event"
                    @update:model-value="onRecipePicked"
                >
                    <template #item="{props: itemProps, item}">
                        <v-list-item
                            v-bind="itemProps"
                            :active="item.raw.value === selectedRecipeName"
                            color="primary"
                        >
                            <div class="io-menu-item">
                                <v-icon
                                    v-if="item.raw.value === selectedRecipeName"
                                    :icon="mdiCheck"
                                    size="small"
                                    class="mr-1"
                                />
                                <recipes-menu-io :ioarray="filterShownRecipeIo(item.raw.recipe.input)" />
                                <v-icon class="d-block" :icon="mdiArrowRight" />
                                <recipes-menu-io :ioarray="filterShownRecipeIo(item.raw.recipe.output)" />
                            </div>
                            <div v-if="allRecipes.length > 1" class="text-caption text-medium-emphasis">
                                {{ item.raw.title }}
                            </div>
                        </v-list-item>
                    </template>
                </v-autocomplete>
            </div>

            <div class="inspector-section">
                <div class="text-caption font-weight-bold text-medium-emphasis">
                    INPUTS (per sec / min as shown)
                </div>
                <div v-for="io in inputs" :key="io.key" class="io-detail-row">
                    <span class="port-badge port-in"><v-icon :icon="mdiArrowRight" size="12" /></span>
                    <icon-component :image="io.image" :data-tooltip="io.label" />
                    <div class="io-detail-text">
                        <div class="io-detail-name">{{ io.label }}</div>
                        <div class="io-detail-rate">
                            <span>{{ ioRate(io) }}</span>
                            <span
                                v-if="ioTransport(io)"
                                class="transport-caption"
                                :class="{
                                    'transport-warn-tight': ioTransport(io)?.warning === 'tight',
                                    'transport-warn-over': ioTransport(io)?.warning === 'over',
                                }"
                                :title="ioTransport(io)?.title"
                            >{{ ioTransport(io)?.label }}</span>
                        </div>
                        <div
                            v-if="ioWarning(io)"
                            class="io-detail-warning"
                            :class="`io-detail-warning-${ioWarning(io)?.kind}`"
                        >
                            {{ ioWarning(io)?.text }}
                        </div>
                    </div>
                </div>
                <div v-if="!inputs.length" class="text-caption text-medium-emphasis">
                    No inputs
                </div>
            </div>

            <div class="inspector-section">
                <div class="text-caption font-weight-bold text-medium-emphasis">
                    OUTPUTS
                </div>
                <div v-for="io in outputs" :key="io.key" class="io-detail-row">
                    <span class="port-badge port-out"><v-icon :icon="mdiArrowRight" size="12" /></span>
                    <icon-component :image="io.image" :data-tooltip="io.label" />
                    <div class="io-detail-text">
                        <div class="io-detail-name">{{ io.label }}</div>
                        <div class="io-detail-rate">
                            <span>{{ ioRate(io) }}</span>
                            <span
                                v-if="ioTransport(io)"
                                class="transport-caption"
                                :class="{
                                    'transport-warn-tight': ioTransport(io)?.warning === 'tight',
                                    'transport-warn-over': ioTransport(io)?.warning === 'over',
                                }"
                                :title="ioTransport(io)?.title"
                            >{{ ioTransport(io)?.label }}</span>
                        </div>
                        <div
                            v-if="ioWarning(io)"
                            class="io-detail-warning"
                            :class="`io-detail-warning-${ioWarning(io)?.kind}`"
                        >
                            {{ ioWarning(io)?.text }}
                        </div>
                    </div>
                </div>
                <div v-if="!outputs.length" class="text-caption text-medium-emphasis">
                    No outputs
                </div>
            </div>

            <div class="inspector-section">
                <div class="text-caption font-weight-bold text-medium-emphasis">
                    OBJECTIVE
                </div>
                <v-btn-toggle color="primary" :model-value="selectedItem.objective">
                    <tooltip-button tooltip="No objective" :icon="mdiCircleOutline" @click="changeObjective(undefined)" />
                    <tooltip-button tooltip="Primary Objective" :icon="mdiBullseyeArrow" @click="changeObjective(Objective.Primary)" />
                    <tooltip-button tooltip="Secondary Objective" :icon="mdiBullseye" @click="changeObjective(Objective.Secondary)" />
                    <tooltip-button tooltip="Low Priority Objective" :icon="mdiPriorityLow" @click="changeObjective(Objective.LowPriority)" />
                </v-btn-toggle>
            </div>

            <div class="inspector-section">
                <div class="text-caption font-weight-bold text-medium-emphasis">
                    FLOW ORIENTATION
                </div>
                <v-btn-toggle
                    :model-value="selectedItem.orientation"
                    color="primary"
                    density="compact"
                    mandatory
                    variant="outlined"
                    @update:model-value="setOrientation"
                >
                    <v-btn value="LR" title="Inputs left, outputs right">
                        LR
                    </v-btn>
                    <v-btn value="RL" title="Inputs right, outputs left">
                        RL
                    </v-btn>
                    <v-btn value="TB" title="Inputs along the top, outputs along the bottom">
                        TB
                    </v-btn>
                    <v-btn value="BT" title="Inputs along the bottom, outputs along the top">
                        BT
                    </v-btn>
                </v-btn-toggle>
            </div>

            <div
                v-if="selectedFamilyRows.length"
                class="inspector-section"
            >
                <div class="text-caption font-weight-bold text-medium-emphasis">
                    UPGRADE / DOWNGRADE
                </div>
                <div
                    v-for="row in selectedFamilyRows"
                    :key="row.factory.name"
                    class="family-row"
                    :title="(row.kind === 'upgrade') ? 'Preview the bigger brother' : 'Preview the smaller brother'"
                    @click="setPreview(row.factory.name)"
                >
                    <span class="family-dir">{{ (row.kind === 'upgrade') ? '↑ Upgrade' : '↓ Downgrade' }}</span>
                    <icon-component :image="row.factory.image" :data-tooltip="row.factory.label" />
                    <span class="family-name">{{ row.factory.label }}</span>
                    <span v-if="row.factory.recipe?.tier !== undefined" class="text-caption text-medium-emphasis">
                        Tier {{ row.factory.recipe?.tier }}
                    </span>
                </div>
            </div>

            <div class="inspector-section d-flex flex-column ga-2">
                <v-btn variant="tonal" :prepend-icon="mdiLinkOff" @click="deleteAllLinks()">
                    Delete all links
                </v-btn>
                <v-btn variant="tonal" color="error" :prepend-icon="mdiDelete" @click="deleteItem">
                    Delete factory
                </v-btn>
            </div>
        </template>
        <template v-else-if="previewItem">
            <div class="d-flex align-center ga-2 mb-1">
                <icon-component :image="previewItem.image" :tooltip="previewItem.label" />
                <div class="min-width-0">
                    <div class="text-subtitle-2 font-weight-bold text-truncate">
                        {{ previewItem.label }}
                    </div>
                    <div class="text-caption text-medium-emphasis text-truncate">
                        {{ previewItem.name }}
                    </div>
                </div>
                <v-spacer />
                <v-chip size="x-small" label color="primary">
                    Preview
                </v-chip>
            </div>

            <div class="inspector-section d-flex flex-column ga-1">
                <div v-if="previewCategory" class="preview-meta">
                    <span class="text-medium-emphasis">Category</span>
                    <span>{{ previewCategory }}</span>
                </div>
                <div v-if="previewTier !== undefined" class="preview-meta">
                    <span class="text-medium-emphasis">Tier</span>
                    <span>Tier {{ previewTier }}</span>
                </div>
            </div>

            <div class="inspector-section">
                <v-btn color="primary" block :prepend-icon="mdiPlus" @click="addPreviewToBoard">
                    Add to board
                </v-btn>
            </div>

            <div
                v-if="previewFamilyRows.length"
                class="inspector-section"
            >
                <div class="text-caption font-weight-bold text-medium-emphasis">
                    UPGRADE / DOWNGRADE
                </div>
                <div
                    v-for="row in previewFamilyRows"
                    :key="row.factory.name"
                    class="family-row"
                    :title="(row.kind === 'upgrade') ? 'Preview the bigger brother' : 'Preview the smaller brother'"
                    @click="setPreview(row.factory.name)"
                >
                    <span class="family-dir">{{ (row.kind === 'upgrade') ? '↑ Upgrade' : '↓ Downgrade' }}</span>
                    <icon-component :image="row.factory.image" :data-tooltip="row.factory.label" />
                    <span class="family-name">{{ row.factory.label }}</span>
                    <span v-if="row.factory.recipe?.tier !== undefined" class="text-caption text-medium-emphasis">
                        Tier {{ row.factory.recipe?.tier }}
                    </span>
                </div>
            </div>

            <div v-if="previewCost.length" class="inspector-section">
                <div class="text-caption font-weight-bold text-medium-emphasis">
                    BUILD COST
                </div>
                <div v-for="(cost, index) in previewCost" :key="index" class="io-rate-row">
                    <icon-component :image="cost.product?.image || ''" :data-tooltip="cost.product?.label" />
                    <span class="io-rate-name">{{ cost.product?.label || '—' }}</span>
                    <span class="text-caption">×{{ formatNumber(cost.count) }}</span>
                </div>
            </div>

            <div class="inspector-section">
                <div class="text-caption font-weight-bold text-medium-emphasis">
                    RECIPES
                </div>
                <div
                    v-for="(recipe, recipeIndex) in previewRecipes"
                    :key="recipe.name"
                    class="preview-recipe"
                >
                    <div v-if="(previewRecipes.length > 1)" class="text-caption font-weight-bold mb-1">
                        {{ recipe.longName || `Recipe ${recipeIndex + 1}` }}
                    </div>
                    <div
                        v-for="io in filterShownRecipeIo(recipe.input)"
                        :key="`in-${io.name}`"
                        class="io-rate-row"
                    >
                        <span class="port-badge port-in"><v-icon :icon="mdiArrowDown" size="12" /></span>
                        <icon-component :image="io.product.image" :data-tooltip="io.product.label" />
                        <span class="io-rate-name">{{ io.product.label }}</span>
                        <span class="text-caption">×{{ formatNumber(io.count) }}</span>
                    </div>
                    <div
                        v-for="io in filterShownRecipeIo(recipe.output)"
                        :key="`out-${io.name}`"
                        class="io-rate-row"
                    >
                        <span class="port-badge port-out"><v-icon :icon="mdiArrowUp" size="12" /></span>
                        <icon-component :image="io.product.image" :data-tooltip="io.product.label" />
                        <span class="io-rate-name">{{ io.product.label }}</span>
                        <span class="text-caption">×{{ formatNumber(io.count) }}</span>
                    </div>
                </div>
                <div v-if="!previewRecipes.length" class="text-caption text-medium-emphasis">
                    No recipes
                </div>
            </div>
        </template>
        <template v-else>
            <div class="text-subtitle-2 font-weight-bold mb-2">
                Inspector
            </div>
            <p class="text-body-2 text-medium-emphasis">
                Click a factory on the board to see its details here — recipe,
                counts, input/output rates — and to rotate or delete it.
            </p>
            <p class="text-body-2 text-medium-emphasis">
                Click a factory in the left panel to preview it here — its
                category, build cost and recipes — then add it to the board
                from the preview. Or drag it from the left panel straight
                onto the grid.
            </p>
            <p class="text-body-2 text-medium-emphasis">
                Drag from a factory's coloured port badge (or its product icon)
                onto another factory to connect them; the ring shows whether
                the connection is valid before you drop.
            </p>
            <v-divider class="my-3" />
            <div class="text-caption text-medium-emphasis">
                Factories on board: {{ itemCount }}
            </div>
            <div class="text-caption text-medium-emphasis">
                Links: {{ linkCount }}
            </div>
        </template>
    </div>
</template>

<style scoped>
.inspector-panel {
    flex: 0 0 300px;
    width: 300px;
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 12px;
    border-left: 1px solid rgba(128, 128, 128, 0.35);
    box-sizing: border-box;
}
.inspector-section {
    margin-top: 14px;
}
.inspector-section > .text-caption {
    margin-bottom: 6px;
    letter-spacing: 0.06em;
}
.inspector-recipe-list {
    max-height: 260px;
    overflow-y: auto;
    background: transparent;
}
.io-menu-item {
    display: flex;
    align-items: center;
}
.io-rate-row {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 2px 0;
}
.io-rate-name {
    font-size: 0.8rem;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
/* fork rework: selected-item IO rows are two-line blocks — the full
   product name (never truncated) on its own line, the rate under it,
   and the transport suggestion as a subtle right-aligned caption on
   the rate line instead of a floating pill */
.io-detail-row {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    padding: 3px 0;
}
.io-detail-row .port-badge {
    margin-top: 1px;
}
.io-detail-text {
    flex: 1;
    min-width: 0;
}
.io-detail-name {
    font-size: 0.82rem;
    font-weight: 600;
    line-height: 1.3;
    overflow-wrap: break-word;
}
.io-detail-rate {
    display: flex;
    align-items: baseline;
    gap: 8px;
    font-size: 0.72rem;
    color: rgba(128, 128, 128, 0.95);
    margin-top: 1px;
}
.transport-caption {
    margin-left: auto;
    white-space: nowrap;
    font-style: italic;
}
/* fork addition: transport capacity warning states on the caption
   (same helper as the link chips — see scripts/transport.ts) */
.transport-caption.transport-warn-tight {
    color: #e65100;
    font-weight: 700;
    font-style: normal;
}
.transport-caption.transport-warn-over {
    color: #c62828;
    font-weight: 700;
    font-style: normal;
}
.v-theme--dark .transport-caption.transport-warn-tight {
    color: #ffb74d;
}
.v-theme--dark .transport-caption.transport-warn-over {
    color: #ef9a9a;
}
/* fork addition: STARVED / BLOCKED warning line on the flagged io
   row ("needs X · linked supply Y" / "makes X · taken Y") */
.io-detail-warning {
    font-size: 0.72rem;
    font-weight: 700;
    margin-top: 1px;
}
.io-detail-warning-starved {
    color: #e65100;
}
.io-detail-warning-blocked {
    color: #c62828;
}
.v-theme--dark .io-detail-warning-starved {
    color: #ffb74d;
}
.v-theme--dark .io-detail-warning-blocked {
    color: #ef9a9a;
}
/* fork addition: upgrade / downgrade family rows */
.family-row {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 3px 4px;
    border-radius: 4px;
    cursor: pointer;
}
.family-row:hover {
    background: rgba(128, 128, 128, 0.15);
}
.family-dir {
    flex: 0 0 92px;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    opacity: 0.65;
}
.family-name {
    flex: 1;
    min-width: 0;
    font-size: 0.82rem;
    font-weight: 600;
    overflow-wrap: break-word;
}
.count-value {
    min-width: 2ch;
    text-align: center;
}
.port-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    color: #fff;
}
.port-in {
    background-color: #00897b;
}
.port-out {
    background-color: #ef6c00;
}
.min-width-0 {
    min-width: 0;
}
/* fork addition: left-panel preview */
.preview-meta {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    font-size: 0.8rem;
    padding: 1px 0;
}
.preview-recipe {
    border: 1px solid rgba(128, 128, 128, 0.3);
    border-radius: 6px;
    padding: 6px 8px;
    margin-bottom: 8px;
}
/* fork addition (Stage I): phone — one screen at a time. The
   inspector is a full-screen step: hidden entirely while nothing
   is selected or previewed (the board owns the screen), and when
   active it covers the viewport with its own Back bar. Every rule
   lives inside the media query; the desktop 300px side panel is
   untouched. */
.inspector-mobile-bar {
    display: none;
}
@media (max-width: 900px) {
    .inspector-mobile-bar {
        display: flex;
        align-items: center;
        gap: 4px;
        position: sticky;
        top: -12px;
        z-index: 2;
        margin: -12px -12px 10px;
        padding: 6px 8px;
        background: rgb(var(--v-theme-surface));
        border-bottom: 1px solid rgba(128, 128, 128, 0.35);
    }
    .inspector-panel {
        display: none;
    }
    .inspector-panel.inspector-active {
        display: block;
        position: fixed;
        inset: 0;
        z-index: 2000;
        width: 100%;
        height: 100dvh;
        flex: none;
        background: rgb(var(--v-theme-surface));
        border-left: none;
    }
}
</style>
